// src/components/Pages/EpistemicAuditPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box, Container, Title, Grid, Paper, Text, Badge, Group, Button,
    Accordion, ScrollArea, Center, Loader, Divider, useMantineColorScheme,
    Modal, Code, SimpleGrid, ThemeIcon, Tooltip, Stack, CopyButton, ActionIcon
} from '@mantine/core';
import {
    IconCheck, IconAlertTriangle, IconClock, IconBrain, IconSearch,
    IconTags, IconArrowsMaximize, IconArrowsMinimize, IconCode,
    IconArrowLeft, IconExternalLink, IconUser, IconBuilding, IconMapPin,
    IconCalendarEvent, IconSparkles, IconCube, IconActivity, IconShieldCheck,
    IconCopy, IconDatabase, IconFilterOff, IconLayoutSidebarRightExpand
} from '@tabler/icons-react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import ProtectedRoute from '../ProtectedRoute';
import InternalPageLayout from '../Layout/InternalPageLayout';

// --- Constants ---
const CMS_EDIT_URL_BASE = 'https://controlcentre.loveincorporated.com/msnglobal/Content/Gallery/Edit/';

// --- 1. Temporal Health & Audit Formatter ---
const calculateHealthState = (auditedAt, decayMonths) => {
    if (!auditedAt || decayMonths === undefined || decayMonths === null) {
        return {
            state: 'red',
            label: 'Action Required',
            color: 'red',
            daysUntilExpiry: null,
            ttlText: 'Unscheduled TTL'
        };
    }

    const auditDate = new Date(auditedAt);
    const expiryDate = new Date(auditDate);
    expiryDate.setMonth(expiryDate.getMonth() + Number(decayMonths));

    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    const ttlText = decayMonths === 999 ? 'Permanent (999M)' : `${decayMonths}M Decay Window`;

    if (daysUntilExpiry <= 0) {
        return {
            state: 'red',
            label: 'Action Required',
            color: 'red',
            daysUntilExpiry,
            ttlText,
            deltaLabel: `${Math.abs(daysUntilExpiry)}d Overdue`
        };
    }
    if (daysUntilExpiry <= 30) {
        return {
            state: 'yellow',
            label: 'Review Approaching',
            color: 'yellow',
            daysUntilExpiry,
            ttlText,
            deltaLabel: `${daysUntilExpiry}d Remaining`
        };
    }
    return {
        state: 'green',
        label: 'Verified (TBC)',
        color: 'teal',
        daysUntilExpiry,
        ttlText,
        deltaLabel: `${daysUntilExpiry}d Remaining`
    };
};

const formatAuditDate = (isoString) => {
    if (!isoString) return 'Never Audited';
    try {
        const d = new Date(isoString);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
        return isoString;
    }
};

// --- 2. Semantic Claim Type Taxonomy ---
const getClaimTypeMeta = (claimType = '') => {
    const normalized = claimType.toLowerCase();
    if (normalized.includes('volatile')) {
        return { color: 'violet', label: 'Volatile State', borderHex: 'var(--mantine-color-violet-6)' };
    }
    if (normalized.includes('intrinsic') || normalized.includes('physical')) {
        return { color: 'teal', label: claimType.replace(/_/g, ' '), borderHex: 'var(--mantine-color-teal-6)' };
    }
    if (normalized.includes('historical') || normalized.includes('immutable') || normalized.includes('event')) {
        return { color: 'indigo', label: claimType.replace(/_/g, ' '), borderHex: 'var(--mantine-color-indigo-5)' };
    }
    if (normalized.includes('evaluative') || normalized.includes('flair') || normalized.includes('subjective')) {
        return { color: 'pink', label: claimType.replace(/_/g, ' '), borderHex: 'var(--mantine-color-pink-5)' };
    }
    return { color: 'grape', label: (claimType || 'Extracted Claim').replace(/_/g, ' '), borderHex: 'var(--mantine-color-grape-5)' };
};

// --- 3. Semantic Entity Type Iconography ---
const getEntityTypeMeta = (entityType = '') => {
    const normalized = entityType.toUpperCase();
    if (normalized.includes('PERSON') || normalized.includes('ARTIST') || normalized.includes('AUTHOR')) {
        return { color: 'cyan', Icon: IconUser, label: entityType };
    }
    if (normalized.includes('ORG') || normalized.includes('COMPANY') || normalized.includes('BAND') || normalized.includes('INSTITUTION')) {
        return { color: 'blue', Icon: IconBuilding, label: entityType };
    }
    if (normalized.includes('LOC') || normalized.includes('GPE') || normalized.includes('STATE') || normalized.includes('CITY') || normalized.includes('PLACE')) {
        return { color: 'emerald', mantineColor: 'green', Icon: IconMapPin, label: entityType };
    }
    if (normalized.includes('DATE') || normalized.includes('TIME') || normalized.includes('EVENT')) {
        return { color: 'orange', Icon: IconCalendarEvent, label: entityType };
    }
    if (normalized.includes('WORK') || normalized.includes('SONG') || normalized.includes('ALBUM') || normalized.includes('ASSET')) {
        return { color: 'grape', Icon: IconSparkles, label: entityType };
    }
    return { color: 'blue', Icon: IconCube, label: entityType || 'ENTITY' };
};

export default function EpistemicAuditPage() {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const id = searchParams.get('id');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // --- UI State: Controlled Accordion Expansion, Triage Filter & Sync ---
    const [expandedAccordions, setExpandedAccordions] = useState({});
    const [expandedStaticSlides, setExpandedStaticSlides] = useState([]);
    const [healthFilter, setHealthFilter] = useState('all'); // 'all' | 'red' | 'yellow' | 'green'
    const [syncedStaticSlideId, setSyncedStaticSlideId] = useState(null);
    const [geoModalOpened, setGeoModalOpened] = useState(false);

    useEffect(() => {
        const fetchAuditData = async () => {
            if (!id) return;
            try {
                const db = getFirestore();
                const docRef = doc(db, 'mock_epistemic_audits', id);
                const snapshot = await getDoc(docRef);
                if (snapshot.exists()) {
                    setData(snapshot.data());
                }
            } catch (error) {
                console.error("EKG Fetch Error:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAuditData();
    }, [id]);

    const { activeSlides, staticSlides, staticSlideIdSet, jsonLdPayload, telemetry } = useMemo(() => {
        const active = [];
        const staticContext = [];
        const staticIds = new Set();
        const hierarchicalSchemaParts = [];

        let totalClaims = 0;
        let volatileCount = 0;
        let staticCount = 0;
        let redCount = 0;
        let yellowCount = 0;
        let greenCount = 0;
        const uniqueEntities = new Set();

        if (data && data.slides) {
            const sortedSlides = [...data.slides].sort((a, b) => a.target_slide_number - b.target_slide_number);

            sortedSlides.forEach(slide => {
                const activeAssertionsForSlide = [];
                const staticAssertionsForSlide = [];
                const slideSchemaClaims = [];

                slide.assertions?.forEach(assertion => {
                    if (assertion.status === 'archived' || assertion.status === 'superseded') return;

                    totalClaims += 1;
                    if (assertion.canonical_name) {
                        uniqueEntities.add(assertion.canonical_name);
                    }

                    // "Full Fat" GEO Payload: Upgraded to respect ERD Entity/Edge relationships
                    slideSchemaClaims.push({
                        "@type": "Claim",
                        "text": assertion.asserted_fact,
                        "additionalType": assertion.claim_type,
                        "description": assertion.verification_query || "Intrinsic fact requiring no verification query.",
                        "about": {
                            "@type": "Thing",
                            "name": assertion.canonical_name || "Extracted Entity",
                            "description": assertion.entity_type
                        },
                        "expires": assertion.triage_decay_months
                            ? (assertion.triage_decay_months === 999 ? "Never" : `P${assertion.triage_decay_months}M`)
                            : undefined,
                        "mentions": assertion.referenced_entities?.length > 0 ? assertion.referenced_entities.map(ent => ({
                            "@type": "Thing",
                            "name": ent.name,
                            "roleName": ent.role,
                            "disambiguatingDescription": ent.relationship ? ent.relationship.replace(/_/g, ' ') : undefined
                        })) : undefined
                    });

                    // Route claims to their respective pane arrays for the UI
                    if (assertion.claim_type === 'volatile_state') {
                        volatileCount += 1;
                        const h = calculateHealthState(assertion.triage_audited_at, assertion.triage_decay_months);
                        if (h.state === 'red') redCount += 1;
                        else if (h.state === 'yellow') yellowCount += 1;
                        else greenCount += 1;

                        activeAssertionsForSlide.push({ ...assertion, _health: h });
                    } else {
                        staticCount += 1;
                        staticAssertionsForSlide.push(assertion);
                    }
                });

                // Construct the structural Slide node for the JSON-LD tree
                hierarchicalSchemaParts.push({
                    "@type": "CreativeWork",
                    "name": slide.slide_title,
                    "text": slide.slide_copy || undefined,
                    "position": slide.target_slide_number,
                    "hasPart": slideSchemaClaims
                });

                if (activeAssertionsForSlide.length > 0) {
                    active.push({ ...slide, assertions: activeAssertionsForSlide });
                }
                if (staticAssertionsForSlide.length > 0) {
                    staticContext.push({ ...slide, assertions: staticAssertionsForSlide });
                    staticIds.add(String(slide.slide_id));
                }
            });
        }

        const geoPayload = {
            "@context": "https://schema.org",
            "@type": "Dataset",
            "name": data?.title || id,
            "description": "Structured intrinsic facts verified via Love Inc's Epistemic Knowledge Graph (EKG) proprietary editorial engine.",
            "hasPart": hierarchicalSchemaParts
        };

        return {
            activeSlides: active,
            staticSlides: staticContext,
            staticSlideIdSet: staticIds,
            jsonLdPayload: geoPayload,
            telemetry: {
                totalSlides: data?.slides?.length || 0,
                totalClaims,
                volatileCount,
                staticCount,
                redCount,
                yellowCount,
                greenCount,
                uniqueEntitiesCount: uniqueEntities.size
            }
        };
    }, [data, id]);

    // --- Derived Filtered Active Slides based on KPI Health Triage Filter ---
    const filteredActiveSlides = useMemo(() => {
        if (healthFilter === 'all') return activeSlides;
        return activeSlides
            .map(slide => {
                const matchingAssertions = slide.assertions.filter(a => {
                    const h = a._health || calculateHealthState(a.triage_audited_at, a.triage_decay_months);
                    return h.state === healthFilter;
                });
                return matchingAssertions.length > 0
                    ? { ...slide, assertions: matchingAssertions }
                    : null;
            })
            .filter(Boolean);
    }, [activeSlides, healthFilter]);

    // Pre-expand the first static slide on mount for zero-flicker contextual grounding
    // useEffect(() => {
    //     if (staticSlides.length > 0 && expandedStaticSlides.length === 0) {
    //         setExpandedStaticSlides([String(staticSlides[0].slide_id)]);
    //     }
    // }, [staticSlides]);

    // --- Interactive KPI Triage Filter Handler ---
    const handleHealthFilterToggle = (targetState) => {
        const nextFilter = healthFilter === targetState ? 'all' : targetState;
        setHealthFilter(nextFilter);

        if (nextFilter === 'all') {
            setExpandedAccordions({});
            return;
        }

        // Auto-expand all matching volatile claim accordions when filtering by Red, Yellow, or Green
        const autoExpanded = {};
        activeSlides.forEach(slide => {
            const matchingIds = slide.assertions
                .filter(a => {
                    const h = a._health || calculateHealthState(a.triage_audited_at, a.triage_decay_months);
                    return h.state === nextFilter;
                })
                .map(a => String(a.assertion_id));

            if (matchingIds.length > 0) {
                autoExpanded[String(slide.slide_id)] = matchingIds;
            }
        });
        setExpandedAccordions(autoExpanded);
    };

    // --- Cross-Pane Narrative Sync Handler ---
    const syncStaticSlide = (slideId) => {
        const key = String(slideId);
        setExpandedStaticSlides(prev => (prev.includes(key) ? prev : [...prev, key]));
        setSyncedStaticSlideId(key);

        setTimeout(() => {
            const el = document.getElementById(`static-slide-${key}`);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 60);

        setTimeout(() => {
            setSyncedStaticSlideId(prev => (prev === key ? null : prev));
        }, 2200);
    };

    const toggleArticleExpansion = (expand) => {
        if (!expand) {
            setExpandedAccordions({});
            return;
        }
        const allExpanded = {};
        filteredActiveSlides.forEach(slide => {
            allExpanded[String(slide.slide_id)] = slide.assertions.map(a => String(a.assertion_id));
        });
        setExpandedAccordions(allExpanded);
    };

    const toggleSlideExpansion = (slideId, expand, assertions) => {
        setExpandedAccordions(prev => ({
            ...prev,
            [String(slideId)]: expand ? assertions.map(a => String(a.assertion_id)) : []
        }));
    };

    const toggleAllStaticSlides = (expand) => {
        if (!expand) {
            setExpandedStaticSlides([]);
            return;
        }
        setExpandedStaticSlides(staticSlides.map(s => String(s.slide_id)));
    };

    if (loading) return <Center h="100vh"><Loader size="lg" color="violet" /></Center>;
    if (!data) return <Center h="100vh"><Text c="dimmed">No EKG Data Found for ID: {id}</Text></Center>;

    const targetOriginId = data.original_id || id;
    const targetGalleryId = data.gallery_id || 'N/A';

    return (
        <ProtectedRoute allowedRoles={['editor','admin', 'management']}>
            <InternalPageLayout>
                {/* Invisible GEO JSON-LD Injection */}
                <script type="application/ld+json">
                    {JSON.stringify(jsonLdPayload)}
                </script>

                <Container fluid px="xl" py="md">
                    {/* Top Navigation & Lineage Bar */}
                    <Group justify="space-between" align="center" mb="xs">
                        <Group gap="xs">
                            <Button
                                variant="subtle"
                                color="gray"
                                size="compact-sm"
                                leftSection={<IconArrowLeft size={16} />}
                                onClick={() => navigate('/resell-blacklist?tab=update')}
                            >
                                Return to Update Queue
                            </Button>
                            <Divider orientation="vertical" />
                            <Badge color="gray" variant="light" size="sm" leftSection={<IconBrain size={12} />}>
                                EKG v0.1 Showcase • Read-Only • Claims Decay & Status Taken from first pass • dates not yet entirely relevant / pending cycle 'bedding-in'
                            </Badge>
                        </Group>

                        <Group gap="xs">
                            <CopyButton value={String(targetOriginId)} timeout={1500}>
                                {({ copied, copy }) => (
                                    <Tooltip label={copied ? "Copied Original ID!" : "Click to copy Original ID"} withArrow>
                                        <Badge
                                            color={copied ? 'teal' : 'gray'}
                                            variant="outline"
                                            size="md"
                                            style={{ cursor: 'pointer', fontFamily: 'monospace' }}
                                            onClick={copy}
                                            rightSection={<IconCopy size={12} />}
                                        >
                                            ORIG ID: {targetOriginId}
                                        </Badge>
                                    </Tooltip>
                                )}
                            </CopyButton>

                            <CopyButton value={String(targetGalleryId)} timeout={1500}>
                                {({ copied, copy }) => (
                                    <Tooltip label={copied ? "Copied Variant Gallery ID!" : "Click to copy Variant Gallery ID"} withArrow>
                                        <Badge
                                            color={copied ? 'teal' : 'gray'}
                                            variant="outline"
                                            size="md"
                                            style={{ cursor: 'pointer', fontFamily: 'monospace' }}
                                            onClick={copy}
                                            rightSection={<IconCopy size={12} />}
                                        >
                                            GALLERY ID: {targetGalleryId}
                                        </Badge>
                                    </Tooltip>
                                )}
                            </CopyButton>
                        </Group>
                    </Group>

                    {/* Master Gallery Title H1 & Action Triggers */}
                    <Group
                        justify="space-between"
                        align="flex-end"
                        mb="md"
                        style={{
                            borderBottom: isDark ? '1px solid var(--mantine-color-dark-4)' : '1px solid var(--mantine-color-gray-3)',
                            paddingBottom: '12px'
                        }}
                    >
                        <Box style={{ flex: 1 }}>
                            <Title order={1} size="h2" c={isDark ? 'violet.4' : 'violet.9'}>
                                {data.title}
                            </Title>
                        </Box>

                        <Group gap="sm">
                            <Tooltip label={`Edit Canonical Original (${targetOriginId}) in Control Centre`} withArrow>
                                <Button
                                    component="a"
                                    href={`${CMS_EDIT_URL_BASE}${targetOriginId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="light"
                                    color="gray"
                                    size="sm"
                                    leftSection={<IconExternalLink size={16} />}
                                >
                                    CMS: Original ({targetOriginId})
                                </Button>
                            </Tooltip>

                            <Tooltip label={`Edit Variant Gallery (${targetGalleryId}) in Control Centre`} withArrow>
                                <Button
                                    component="a"
                                    href={`${CMS_EDIT_URL_BASE}${targetGalleryId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="light"
                                    color="blue"
                                    size="sm"
                                    leftSection={<IconExternalLink size={16} />}
                                >
                                    CMS: Gallery ({targetGalleryId})
                                </Button>
                            </Tooltip>

                            <Button
                                variant="filled"
                                color="violet"
                                size="sm"
                                leftSection={<IconCode size={16} />}
                                onClick={() => setGeoModalOpened(true)}
                            >
                                View GEO Payload
                            </Button>
                        </Group>
                    </Group>

                    {/* Executive Telemetry KPI Ribbon */}
                    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" mb="lg">
                        <Paper
                            withBorder
                            p="sm"
                            radius="md"
                            bg={isDark ? 'dark.7' : 'gray.0'}
                            style={{
                                borderColor: healthFilter !== 'all' ? 'var(--mantine-color-violet-5)' : undefined
                            }}
                        >
                            <Group justify="space-between" wrap="nowrap">
                                <Box>
                                    <Group gap={6} align="center">
                                        <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Volatile Triage Queue</Text>
                                        {healthFilter !== 'all' && (
                                            <Tooltip label="Reset Triage Filter" withArrow>
                                                <ActionIcon
                                                    size="xs"
                                                    variant="subtle"
                                                    color="violet"
                                                    onClick={() => handleHealthFilterToggle('all')}
                                                >
                                                    <IconFilterOff size={12} />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </Group>
                                    <Group gap="xs" align="baseline" mt={4}>
                                        <Text size="xl" fw={800} c={telemetry.redCount > 0 ? 'red.5' : (isDark ? 'white' : 'dark')}>
                                            {telemetry.volatileCount}
                                        </Text>
                                        <Text size="xs" c="dimmed">active claims (click to filter)</Text>
                                    </Group>
                                </Box>
                                <Group gap={4}>
                                    <Tooltip label="Filter: Action Required (Expired / Unaudited)" withArrow>
                                        <Badge
                                            color="red"
                                            variant={healthFilter === 'red' ? 'filled' : (healthFilter === 'all' ? 'filled' : 'outline')}
                                            size="sm"
                                            onClick={() => handleHealthFilterToggle('red')}
                                            style={{
                                                cursor: 'pointer',
                                                opacity: healthFilter === 'all' || healthFilter === 'red' ? 1 : 0.45,
                                                transform: healthFilter === 'red' ? 'scale(1.06)' : 'none'
                                            }}
                                        >
                                            {telemetry.redCount} Red
                                        </Badge>
                                    </Tooltip>
                                    <Tooltip label="Filter: Review Approaching (<= 30 Days)" withArrow>
                                        <Badge
                                            color="yellow"
                                            variant={healthFilter === 'yellow' ? 'filled' : (healthFilter === 'all' ? 'filled' : 'outline')}
                                            size="sm"
                                            onClick={() => handleHealthFilterToggle('yellow')}
                                            style={{
                                                cursor: 'pointer',
                                                opacity: healthFilter === 'all' || healthFilter === 'yellow' ? 1 : 0.45,
                                                transform: healthFilter === 'yellow' ? 'scale(1.06)' : 'none'
                                            }}
                                        >
                                            {telemetry.yellowCount} Due
                                        </Badge>
                                    </Tooltip>
                                    <Tooltip label="Filter: Verified TBC" withArrow>
                                        <Badge
                                            color="teal"
                                            variant={healthFilter === 'green' ? 'filled' : (healthFilter === 'all' ? 'filled' : 'outline')}
                                            size="sm"
                                            onClick={() => handleHealthFilterToggle('green')}
                                            style={{
                                                cursor: 'pointer',
                                                opacity: healthFilter === 'all' || healthFilter === 'green' ? 1 : 0.45,
                                                transform: healthFilter === 'green' ? 'scale(1.06)' : 'none'
                                            }}
                                        >
                                            {telemetry.greenCount} (TBC)
                                        </Badge>
                                    </Tooltip>
                                </Group>
                            </Group>
                        </Paper>

                        <Paper withBorder p="sm" radius="md" bg={isDark ? 'dark.7' : 'gray.0'}>
                            <Group justify="space-between" wrap="nowrap">
                                <Box>
                                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Static Context Graph</Text>
                                    <Group gap="xs" align="baseline" mt={4}>
                                        <Text size="xl" fw={800} c="teal.5">{telemetry.staticCount}</Text>
                                        <Text size="xs" c="dimmed">evergreen assertions</Text>
                                    </Group>
                                </Box>
                                <ThemeIcon variant="light" color="teal" size="lg" radius="md">
                                    <IconShieldCheck size={20} />
                                </ThemeIcon>
                            </Group>
                        </Paper>

                        <Paper withBorder p="sm" radius="md" bg={isDark ? 'dark.7' : 'gray.0'}>
                            <Group justify="space-between" wrap="nowrap">
                                <Box>
                                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Canonical Entities</Text>
                                    <Group gap="xs" align="baseline" mt={4}>
                                        <Text size="xl" fw={800} c="blue.5">{telemetry.uniqueEntitiesCount}</Text>
                                        <Text size="xs" c="dimmed">indexed across {telemetry.totalSlides} slides</Text>
                                    </Group>
                                </Box>
                                <ThemeIcon variant="light" color="blue" size="lg" radius="md">
                                    <IconDatabase size={20} />
                                </ThemeIcon>
                            </Group>
                        </Paper>

                        <Paper withBorder p="sm" radius="md" bg={isDark ? 'dark.7' : 'gray.0'}>
                            <Group justify="space-between" wrap="nowrap">
                                <Box>
                                    <Text size="xs" c="dimmed" tt="uppercase" fw={700}>GEO JSON-LD Nodes</Text>
                                    <Group gap="xs" align="baseline" mt={4}>
                                        <Text size="xl" fw={800} c="violet.5">{telemetry.totalClaims}</Text>
                                        <Text size="xs" c="dimmed">Schema.org claims mounted</Text>
                                    </Group>
                                </Box>
                                <ThemeIcon variant="light" color="violet" size="lg" radius="md">
                                    <IconActivity size={20} />
                                </ThemeIcon>
                            </Group>
                        </Paper>
                    </SimpleGrid>

                    <Grid gutter="xl">
                        {/* Left Pane: Active Audit Queue */}
                        <Grid.Col span={8}>
                            <Paper withBorder shadow="sm" p="md" radius="md" h="70vh" style={{ display: 'flex', flexDirection: 'column' }} bg={isDark ? 'dark.8' : 'white'}>
                                <Group justify="space-between" align="center" mb="sm">
                                    <Group gap="xs">
                                        <ThemeIcon color="violet" variant="light" size="md" radius="sm">
                                            <IconBrain size={18} />
                                        </ThemeIcon>
                                        <Title order={4}>Volatile Claims Queue</Title>
                                        <Badge color="violet" variant="light" size="sm">
                                            {filteredActiveSlides.length} {filteredActiveSlides.length === 1 ? 'Slide' : 'Slides'} Shown
                                        </Badge>
                                        {healthFilter !== 'all' && (
                                            <Badge
                                                color={healthFilter === 'red' ? 'red' : (healthFilter === 'yellow' ? 'yellow' : 'teal')}
                                                variant="filled"
                                                size="sm"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleHealthFilterToggle('all')}
                                                rightSection={<IconFilterOff size={10} />}
                                            >
                                                Filter: {healthFilter.toUpperCase()} (Reset)
                                            </Badge>
                                        )}
                                    </Group>
                                    <Group gap="xs">
                                        <Button size="compact-xs" variant="light" color="violet" onClick={() => toggleArticleExpansion(true)} leftSection={<IconArrowsMaximize size={14} />}>
                                            Expand All
                                        </Button>
                                        <Button size="compact-xs" variant="light" color="gray" onClick={() => toggleArticleExpansion(false)} leftSection={<IconArrowsMinimize size={14} />}>
                                            Collapse All
                                        </Button>
                                    </Group>
                                </Group>
                                <Divider mb="md" />

                                <ScrollArea flex={1} type="auto" offsetScrollbars pr="xs">
                                    {filteredActiveSlides.length === 0 ? (
                                        <Center py="xl">
                                            <Stack align="center" gap="xs">
                                                <Text size="sm" c="dimmed">
                                                    No volatile claims match the selected "{healthFilter.toUpperCase()}" health filter.
                                                </Text>
                                                <Button size="xs" variant="light" color="violet" onClick={() => handleHealthFilterToggle('all')}>
                                                    Show All Volatile Claims
                                                </Button>
                                            </Stack>
                                        </Center>
                                    ) : (
                                        filteredActiveSlides.map(slide => {
                                            const isSlideFullyExpanded = expandedAccordions[String(slide.slide_id)]?.length === slide.assertions.length;
                                            const hasStaticCompanion = staticSlideIdSet.has(String(slide.slide_id));

                                            return (
                                                <Box key={String(slide.slide_id)} mb="xl">
                                                    {/* Slide Header with Localized Expansion & Right-Pane Sync Control */}
                                                    <Group justify="space-between" mb="xs" align="center">
                                                        <Group gap="xs" align="center">
                                                            <Badge color="violet" variant="filled" size="md" radius="sm">
                                                                SLIDE {slide.target_slide_number}
                                                            </Badge>
                                                            <Title order={5} c={isDark ? 'gray.1' : 'dark.9'}>
                                                                {slide.slide_title}
                                                            </Title>
                                                            <Text size="xs" c="dimmed" ff="monospace">
                                                                (#{slide.slide_id})
                                                            </Text>
                                                        </Group>
                                                        <Group gap="xs">
                                                            {hasStaticCompanion && (
                                                                <Tooltip label="Expand & scroll to this slide's Static Context in the Right Pane" withArrow>
                                                                    <Button
                                                                        size="compact-xs"
                                                                        variant="light"
                                                                        color="teal"
                                                                        leftSection={<IconLayoutSidebarRightExpand size={13} />}
                                                                        onClick={() => syncStaticSlide(slide.slide_id)}
                                                                    >
                                                                        Sync Static Context
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            <Button
                                                                size="compact-xs"
                                                                variant="subtle"
                                                                color="violet"
                                                                onClick={() => toggleSlideExpansion(slide.slide_id, !isSlideFullyExpanded, slide.assertions)}
                                                            >
                                                                {isSlideFullyExpanded ? "Collapse Slide" : `Expand Slide (${slide.assertions.length})`}
                                                            </Button>
                                                        </Group>
                                                    </Group>

                                                    {/* Master Slide Container */}
                                                    <Paper
                                                        withBorder
                                                        bg={isDark ? 'dark.7' : 'gray.0'}
                                                        p="md"
                                                        radius="md"
                                                    >
                                                        {/* Narrative Copy Quote Box */}
                                                        <Box
                                                            mb="md"
                                                            p="sm"
                                                            style={{
                                                                borderLeft: '4px solid var(--mantine-color-violet-5)',
                                                                backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                                                                borderRadius: '0 6px 6px 0'
                                                            }}
                                                        >
                                                            <Text size="xs" tt="uppercase" fw={700} c="dimmed" mb={4}>
                                                                Published Slide Copy
                                                            </Text>
                                                            <Text size="sm" lh="1.6" c={isDark ? 'gray.2' : 'dark.8'} fs="italic">
                                                                {slide.slide_copy || "No slide copy provided."}
                                                            </Text>
                                                        </Box>

                                                        {/* Controlled Accordion State */}
                                                        <Accordion
                                                            variant="separated"
                                                            multiple
                                                            value={expandedAccordions[String(slide.slide_id)] || []}
                                                            onChange={(val) => setExpandedAccordions(prev => ({ ...prev, [String(slide.slide_id)]: val }))}
                                                        >
                                                            {slide.assertions.map(assertion => {
                                                                const health = assertion._health || calculateHealthState(assertion.triage_audited_at, assertion.triage_decay_months);
                                                                const entityMeta = getEntityTypeMeta(assertion.entity_type);
                                                                const EntityIcon = entityMeta.Icon;

                                                                const displayQuery = assertion.verification_log?.needs_rewrite
                                                                    ? assertion.verification_log.query_rewrite
                                                                    : assertion.verification_query;

                                                                return (
                                                                    <Accordion.Item
                                                                        key={String(assertion.assertion_id)}
                                                                        value={String(assertion.assertion_id)}
                                                                        bg={isDark ? 'dark.6' : 'violet.0'}
                                                                        style={{
                                                                            border: isDark
                                                                                ? '1px solid var(--mantine-color-dark-4)'
                                                                                : '1px solid var(--mantine-color-violet-2)'
                                                                        }}
                                                                    >
                                                                        <Accordion.Control>
                                                                            <Group justify="space-between" wrap="nowrap" pr="xs">
                                                                                <Group gap="sm" wrap="nowrap" style={{ flex: 1 }}>
                                                                                    <ThemeIcon color={health.color} variant="light" size="sm" radius="xl">
                                                                                        <IconBrain size={14} />
                                                                                    </ThemeIcon>
                                                                                    <Text fw={600} size="sm" c={isDark ? 'gray.1' : 'dark.9'}>
                                                                                        {assertion.asserted_fact}
                                                                                    </Text>
                                                                                </Group>
                                                                                <Group gap="xs" wrap="nowrap">
                                                                                    {health.deltaLabel && (
                                                                                        <Badge color={health.color} variant="outline" size="xs">
                                                                                            {health.deltaLabel}
                                                                                        </Badge>
                                                                                    )}
                                                                                    <Badge color={health.color} variant="dot" size="sm">
                                                                                        {health.label}
                                                                                    </Badge>
                                                                                </Group>
                                                                            </Group>
                                                                        </Accordion.Control>

                                                                        <Accordion.Panel>
                                                                            <Paper bg={isDark ? 'dark.7' : 'white'} p="md" radius="sm" withBorder>
                                                                                {/* Top Metadata & Temporal Telemetry Bar */}
                                                                                <Group justify="space-between" align="center" mb="sm">
                                                                                    <Group gap="xs">
                                                                                        <Badge size="xs" color="violet" variant="light" tt="uppercase">
                                                                                            {assertion.claim_type.replace(/_/g, ' ')}
                                                                                        </Badge>
                                                                                        {assertion.entity_type && (
                                                                                            <Badge
                                                                                                size="xs"
                                                                                                color={entityMeta.mantineColor || entityMeta.color}
                                                                                                variant="light"
                                                                                                tt="uppercase"
                                                                                                leftSection={<EntityIcon size={10} />}
                                                                                            >
                                                                                                {assertion.entity_type}
                                                                                            </Badge>
                                                                                        )}
                                                                                        <Badge size="xs" color="gray" variant="outline" leftSection={<IconClock size={10} />}>
                                                                                            {health.ttlText}
                                                                                        </Badge>
                                                                                    </Group>

                                                                                    <Group gap="sm">
                                                                                        <Text size="xs" c="dimmed">
                                                                                            Audited: <b>{formatAuditDate(assertion.triage_audited_at)}</b>
                                                                                        </Text>
                                                                                        <Text size="xs" c="dimmed" ff="monospace" title="Database Assertion Primary Key">
                                                                                            ID: #{assertion.assertion_id}
                                                                                        </Text>
                                                                                    </Group>
                                                                                </Group>

                                                                                {/* Primary Entity Callout */}
                                                                                {assertion.canonical_name && (
                                                                                    <Group gap="xs" mb="sm" align="center">
                                                                                        <ThemeIcon size="xs" variant="subtle" color="violet">
                                                                                            <EntityIcon size={14} />
                                                                                        </ThemeIcon>
                                                                                        <Text size="sm" fw={600} c={isDark ? 'gray.3' : 'dark.7'}>
                                                                                            Primary Entity:{' '}
                                                                                            <Text span fw={700} c={isDark ? 'violet.3' : 'violet.8'}>
                                                                                                {assertion.canonical_name}
                                                                                            </Text>
                                                                                        </Text>
                                                                                    </Group>
                                                                                )}

                                                                                {/* Related Entities Network Mapping in Left Pane */}
                                                                                {assertion.referenced_entities && assertion.referenced_entities.length > 0 && (
                                                                                    <Group gap="xs" mb="md" align="flex-start" wrap="nowrap">
                                                                                        <Box mt={3}>
                                                                                            <IconTags size={16} color="var(--mantine-color-orange-5)" />
                                                                                        </Box>
                                                                                        <Group gap={6} style={{ flex: 1 }}>
                                                                                            {assertion.referenced_entities.map((ent, idx) => (
                                                                                                <Badge
                                                                                                    key={idx}
                                                                                                    size="xs"
                                                                                                    variant="light"
                                                                                                    color="orange"
                                                                                                    style={{ textTransform: 'none' }}
                                                                                                >
                                                                                                    <b>{ent.name}</b>
                                                                                                    {ent.role ? ` • ${ent.role}` : ''}
                                                                                                    {ent.relationship ? ` (${ent.relationship.replace(/_/g, ' ')})` : ''}
                                                                                                </Badge>
                                                                                            ))}
                                                                                        </Group>
                                                                                    </Group>
                                                                                )}

                                                                                {/* Google Search Fire & Forget Integration */}
                                                                                <Paper
                                                                                    withBorder
                                                                                    p="xs"
                                                                                    radius="sm"
                                                                                    mb="md"
                                                                                    bg={isDark ? 'dark.8' : 'gray.0'}
                                                                                >
                                                                                    <Group gap="xs" align="center" wrap="nowrap">
                                                                                        <IconSearch size={14} color="gray" />
                                                                                        <Text size="sm" c={isDark ? 'gray.2' : 'dark.8'} style={{ flex: 1 }} fs="italic">
                                                                                            <b>Verification Query: </b>
                                                                                            {displayQuery || assertion.asserted_fact}
                                                                                        </Text>
                                                                                    </Group>
                                                                                </Paper>

                                                                                <Group justify="space-between" align="center">
                                                                                    <Group gap="xs">
                                                                                        {displayQuery && (
                                                                                            <Button
                                                                                                component="a"
                                                                                                href={`https://www.google.com/search?q=${encodeURIComponent(displayQuery)}`}
                                                                                                target="_blank"
                                                                                                rel="noreferrer"
                                                                                                size="xs"
                                                                                                variant="filled"
                                                                                                color={health.color}
                                                                                                leftSection={<IconSearch size={12} />}
                                                                                            >
                                                                                                Verify Claim
                                                                                            </Button>
                                                                                        )}
                                                                                        <Tooltip label="Read-Only Mode (Mutations locked in v0.1 Showcase)" withArrow>
                                                                                            <Button size="xs" variant="light" color="teal" leftSection={<IconCheck size={14} />}>
                                                                                                Mark Verified
                                                                                            </Button>
                                                                                        </Tooltip>
                                                                                        <Tooltip label="Read-Only Mode (Conflict Resolution Modal coming in v0.2)" withArrow>
                                                                                            <Button size="xs" variant="light" color="red" leftSection={<IconAlertTriangle size={14} />}>
                                                                                                Flag Conflict
                                                                                            </Button>
                                                                                        </Tooltip>
                                                                                    </Group>
                                                                                </Group>
                                                                            </Paper>
                                                                        </Accordion.Panel>
                                                                    </Accordion.Item>
                                                                );
                                                            })}
                                                        </Accordion>
                                                    </Paper>
                                                </Box>
                                            );
                                        })
                                    )}
                                </ScrollArea>
                            </Paper>
                        </Grid.Col>

                        {/* Right Pane: Static Context & Entities Drawer */}
                        <Grid.Col span={4}>
                            <Paper withBorder shadow="sm" p="md" radius="md" h="70vh" style={{ display: 'flex', flexDirection: 'column' }} bg={isDark ? 'dark.8' : 'white'}>
                                <Group justify="space-between" align="center" mb="sm">
                                    <Group gap="xs">
                                        <ThemeIcon color="teal" variant="light" size="md" radius="sm">
                                            <IconClock size={18} />
                                        </ThemeIcon>
                                        <Title order={5}>Static Context & Entities</Title>
                                    </Group>
                                    <Group gap={4}>
                                        <Tooltip label="Expand All Static Slides">
                                            <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => toggleAllStaticSlides(true)}>
                                                <IconArrowsMaximize size={14} />
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label="Collapse All Static Slides">
                                            <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => toggleAllStaticSlides(false)}>
                                                <IconArrowsMinimize size={14} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                </Group>
                                <Divider mb="md" />

                                <ScrollArea flex={1} type="auto" offsetScrollbars pr="xs">
                                    <Accordion
                                        variant="separated"
                                        multiple
                                        value={expandedStaticSlides}
                                        onChange={setExpandedStaticSlides}
                                    >
                                        {staticSlides.map(slide => {
                                            const isSynced = syncedStaticSlideId === String(slide.slide_id);

                                            return (
                                                <Accordion.Item
                                                    id={`static-slide-${slide.slide_id}`}
                                                    key={String(slide.slide_id)}
                                                    value={String(slide.slide_id)}
                                                    bg={isDark ? 'dark.7' : 'gray.0'}
                                                    style={{
                                                        border: isSynced
                                                            ? '2px solid var(--mantine-color-teal-5)'
                                                            : undefined,
                                                        transition: 'border-color 0.25s ease'
                                                    }}
                                                >
                                                    <Accordion.Control>
                                                        <Group justify="space-between" wrap="nowrap" pr="xs">
                                                            <Group wrap="nowrap" gap="xs" style={{ flex: 1, overflow: 'hidden' }}>
                                                                <Badge color={isSynced ? 'teal' : 'violet'} variant="filled" size="sm" radius="sm">
                                                                    SLIDE {slide.target_slide_number}
                                                                </Badge>
                                                                <Text size="sm" fw={600} c={isDark ? 'gray.2' : 'dark.9'} lineClamp={1} title={slide.slide_title}>
                                                                    {slide.slide_title}
                                                                </Text>
                                                            </Group>
                                                            <Badge color="gray" variant="light" size="xs">
                                                                {slide.assertions.length}
                                                            </Badge>
                                                        </Group>
                                                    </Accordion.Control>

                                                    <Accordion.Panel>
                                                        <Stack gap="sm">
                                                            {slide.assertions.map(fact => {
                                                                const claimMeta = getClaimTypeMeta(fact.claim_type);
                                                                const entityMeta = getEntityTypeMeta(fact.entity_type);
                                                                const EntityIcon = entityMeta.Icon;

                                                                // Entity-Grounded Search Query Fallback
                                                                const checkQuery = fact.verification_query || (
                                                                    fact.canonical_name
                                                                        ? `${fact.canonical_name} ${fact.asserted_fact}`
                                                                        : fact.asserted_fact
                                                                );

                                                                return (
                                                                    <Paper
                                                                        key={String(fact.assertion_id)}
                                                                        p="sm"
                                                                        radius="sm"
                                                                        withBorder
                                                                        style={{ borderLeft: `4px solid ${claimMeta.borderHex}` }}
                                                                        bg={isDark ? 'dark.6' : 'white'}
                                                                    >
                                                                        {/* Primary Entity Header with Dynamic Entity Type Icon */}
                                                                        <Group justify="space-between" align="center" mb={6} wrap="nowrap">
                                                                            {fact.canonical_name ? (
                                                                                <Group gap={6} wrap="nowrap" style={{ overflow: 'hidden' }}>
                                                                                    <ThemeIcon
                                                                                        size="xs"
                                                                                        variant="light"
                                                                                        color={entityMeta.mantineColor || entityMeta.color}
                                                                                        radius="xl"
                                                                                    >
                                                                                        <EntityIcon size={11} />
                                                                                    </ThemeIcon>
                                                                                    <Text size="xs" fw={700} c={isDark ? 'violet.3' : 'violet.8'} truncate>
                                                                                        {fact.canonical_name}
                                                                                    </Text>
                                                                                </Group>
                                                                            ) : <Box />}

                                                                            <Text size="10px" c="dimmed" ff="monospace">
                                                                                #{fact.assertion_id}
                                                                            </Text>
                                                                        </Group>

                                                                        {/* Asserted Intrinsic Fact */}
                                                                        <Text size="sm" fw={500} mb="sm" lh={1.45} c={isDark ? 'gray.2' : 'dark.9'}>
                                                                            {fact.asserted_fact}
                                                                        </Text>

                                                                        {/* Differentiated Claim Badging & Entity-Scoped Double Check */}
                                                                        <Group justify="space-between" align="center" wrap="nowrap">
                                                                            <Group gap={6}>
                                                                                <Badge
                                                                                    size="xs"
                                                                                    color={claimMeta.color}
                                                                                    variant="light"
                                                                                    tt="uppercase"
                                                                                >
                                                                                    {claimMeta.label}
                                                                                </Badge>
                                                                                {fact.entity_type && (
                                                                                    <Badge
                                                                                        size="xs"
                                                                                        color={entityMeta.mantineColor || entityMeta.color}
                                                                                        variant="outline"
                                                                                        tt="uppercase"
                                                                                        leftSection={<EntityIcon size={10} />}
                                                                                    >
                                                                                        {fact.entity_type}
                                                                                    </Badge>
                                                                                )}
                                                                            </Group>

                                                                            <Tooltip label={`Search Google: "${checkQuery}"`} withArrow position="left">
                                                                                <Button
                                                                                    component="a"
                                                                                    href={`https://www.google.com/search?q=${encodeURIComponent(checkQuery)}`}
                                                                                    target="_blank"
                                                                                    rel="noreferrer"
                                                                                    size="compact-xs"
                                                                                    variant="light"
                                                                                    color="teal"
                                                                                    leftSection={<IconSearch size={12} />}
                                                                                >
                                                                                    Double Check
                                                                                </Button>
                                                                            </Tooltip>
                                                                        </Group>

                                                                        {/* Related Entities Network Mapping (Now surfacing Role + Relationship) */}
                                                                        {fact.referenced_entities && fact.referenced_entities.length > 0 && (
                                                                            <Box
                                                                                mt="sm"
                                                                                pt="xs"
                                                                                style={{
                                                                                    borderTop: isDark
                                                                                        ? '1px dashed var(--mantine-color-dark-4)'
                                                                                        : '1px dashed var(--mantine-color-gray-3)'
                                                                                }}
                                                                            >
                                                                                <Group gap={6} align="flex-start" wrap="nowrap">
                                                                                    <Box mt={2}>
                                                                                        <IconTags size={14} color="var(--mantine-color-orange-5)" />
                                                                                    </Box>
                                                                                    <Group gap={4} style={{ flex: 1 }}>
                                                                                        {fact.referenced_entities.map((ent, idx) => (
                                                                                            <Badge
                                                                                                key={idx}
                                                                                                size="xs"
                                                                                                variant="light"
                                                                                                color="orange"
                                                                                                style={{ textTransform: 'none' }}
                                                                                            >
                                                                                                <b>{ent.name}</b>
                                                                                                {ent.role ? ` • ${ent.role}` : ''}
                                                                                                {ent.relationship ? ` (${ent.relationship.replace(/_/g, ' ')})` : ''}
                                                                                            </Badge>
                                                                                        ))}
                                                                                    </Group>
                                                                                </Group>
                                                                            </Box>
                                                                        )}
                                                                    </Paper>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Accordion.Panel>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </ScrollArea>
                            </Paper>
                        </Grid.Col>
                    </Grid>

                    {/* GEO JSON-LD Showcase Modal */}
                    <Modal
                        opened={geoModalOpened}
                        onClose={() => setGeoModalOpened(false)}
                        title={
                            <Group gap="xs">
                                <ThemeIcon color="violet" variant="light" size="md">
                                    <IconCode size={18} />
                                </ThemeIcon>
                                <Title order={4}>Generative Engine Optimisation (GEO) Payload</Title>
                            </Group>
                        }
                        size="xl"
                        centered
                        overlayProps={{ blur: 3, opacity: 0.5 }}
                    >
                        <Text size="sm" mb="md" lh="1.5" c={isDark ? 'gray.3' : 'dark.7'}>
                            <b>For AI Search:</b> This structured Schema.org payload (in JSON-LD format) is computed dynamically in-memory from our Epistemic Knowledge Graph. In production, it sits invisibly in the DOM head to feed verified semantic relationships directly to AI search engines (e.g., SearchGPT by OpenAI, Perplexity).
                        </Text>

                        <Group justify="space-between" mb="xs">
                            <Badge color="teal" variant="light" size="sm">
                                Schema.org Dataset • {telemetry.totalSlides} CreativeWork Nodes • {telemetry.totalClaims} Claims
                            </Badge>
                            <CopyButton value={JSON.stringify(jsonLdPayload, null, 2)} timeout={2000}>
                                {({ copied, copy }) => (
                                    <Button size="compact-xs" color={copied ? 'teal' : 'violet'} variant="light" onClick={copy} leftSection={<IconCopy size={12} />}>
                                        {copied ? 'Copied JSON-LD' : 'Copy Payload'}
                                    </Button>
                                )}
                            </CopyButton>
                        </Group>

                        <ScrollArea h={480} type="auto" offsetScrollbars>
                            <Code
                                block
                                fz="xs"
                                color={isDark ? 'dark.8' : 'gray.1'}
                                c={isDark ? 'teal.3' : 'teal.9'}
                                style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5' }}
                            >
                                {JSON.stringify(jsonLdPayload, null, 2)}
                            </Code>
                        </ScrollArea>
                    </Modal>
                </Container>
            </InternalPageLayout>
        </ProtectedRoute>
    );
}